import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import ContactManagerPanel, { type ContactManagerChangeReason } from "@/components/contacts/ContactManagerPanel";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { sanitizeDatetimeLocal } from "@/lib/datetimeLocal";
import { openNativeDateTimePicker } from "@/lib/dateTimePicker";
import { COUNTRY_OPTIONS } from "@/lib/countries";
import {
  addProspectNote,
  deleteProspectNote,
  getProspect,
  qualifyProspectToPartnerWithBusinessType,
  updateProspect,
  type FinalClientType,
  type Prospect,
  type ProspectBusinessType,
  type ProspectContact,
} from "@/lib/prospectsApi";
import { getLatestVoiceCallStatus, getVoiceCallStatus, startVoiceCall, type VoiceCallStatusResponse } from "@/lib/voiceApi";
import { lookupUsersByRoles } from "@/lib/usersApi";
import {
  finalClientTypeOptions,
  getStageBadgeVariant,
  getStageLabel,
  isValidStage,
  noteTypeOptions,
  stageOptions,
} from "@/components/prospects/prospectConstants";
import { MessageCircle, Phone, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface ProspectDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prospectId: number | null;
  onUpdated?: () => void;
}

interface WhatsAppSelectionState {
  contactName: string;
  phoneOptions: string[];
}

interface CallSelectionState {
  contactName: string;
  phoneOptions: string[];
}

const toLocalInput = (iso?: string | null) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromLocalInput = (value: string) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString();
};

const formatCallStatus = (status?: string | null) => {
  const normalized = (status ?? "").trim().toLowerCase();
  switch (normalized) {
    case "queued":
      return "Queued";
    case "initiated":
      return "Initiated";
    case "ringing":
      return "Ringing";
    case "answered":
    case "in-progress":
      return "In progress";
    case "completed":
      return "Completed";
    case "busy":
      return "Busy";
    case "failed":
      return "Failed";
    case "no-answer":
      return "No answer";
    case "canceled":
      return "Canceled";
    default:
      return status || "Unknown";
  }
};

const parsePhoneOptions = (telephone?: string | null): string[] => {
  const source = telephone?.trim();
  if (!source) return [];

  const normalized = source
    .replace(/\s+(e|and|ou)\s+/gi, ";")
    .replace(/[\n\r]+/g, ";")
    .replace(/[|/,]+/g, ";");

  const chunks = normalized
    .split(";")
    .map((item) => item.trim())
    .filter(Boolean);

  const values = chunks.length > 0 ? chunks : [source];
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const value of values) {
    const key = value.replace(/\D/g, "") || value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(value);
  }
  return unique;
};

const normalizePhoneForWhatsApp = (value: string) => value.replace(/\D/g, "");

const normalizeContacts = (contacts: ProspectContact[]) =>
  contacts.map((contact) => ({
    ...contact,
    is_primary: Boolean(contact.is_primary),
    no_response: Boolean(contact.no_response ?? (contact as { noResponse?: boolean | null }).noResponse),
  }));

const enforceSinglePriority = (contacts: ProspectContact[]) => {
  let foundPrimary = false;
  return contacts.map((contact) => {
    if (contact.is_primary && !foundPrimary) {
      foundPrimary = true;
      return contact;
    }
    if (contact.is_primary && foundPrimary) {
      return { ...contact, is_primary: false };
    }
    return contact;
  });
};

type FinalClientTypeInput =
  | (FinalClientType | string | null | undefined)[]
  | FinalClientType
  | string
  | null
  | undefined;

const normalizeFinalClientTypes = (values?: FinalClientTypeInput): FinalClientType[] => {
  if (!values) return [];
  const allowed = new Set(finalClientTypeOptions.map((option) => option.value));
  const unique = new Set<FinalClientType>();
  const rawValues = Array.isArray(values)
    ? values
    : String(values)
        .split(/[;,]/)
        .map((item) => item.trim())
        .filter(Boolean);
  for (const value of rawValues) {
    if (!value) continue;
    const normalized = String(value).trim().toLowerCase().replace(/-/g, "_").replace(/\s+/g, "_");
    if (allowed.has(normalized)) {
      unique.add(normalized as FinalClientType);
    }
  }
  return Array.from(unique);
};

const ProspectDetailDialog = ({ open, onOpenChange, prospectId, onUpdated }: ProspectDetailDialogProps) => {
  const { toast } = useToast();
  const { userRole, user } = useAuth();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteType, setNoteType] = useState("note");
  const [stage, setStage] = useState("");
  const [businessType, setBusinessType] = useState<ProspectBusinessType | "">("");
  const [finalClientTypes, setFinalClientTypes] = useState<FinalClientType[]>([]);
  const [finalClientTypePicker, setFinalClientTypePicker] = useState<FinalClientType | "">("");
  const [qualifyBusinessType, setQualifyBusinessType] = useState<ProspectBusinessType | "">("");
  const [qualifyFinalClientTypes, setQualifyFinalClientTypes] = useState<FinalClientType[]>([]);
  const [qualifyFinalClientTypePicker, setQualifyFinalClientTypePicker] = useState<FinalClientType | "">("");
  const [nextFollowUp, setNextFollowUp] = useState<string>("");
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [ownerOptions, setOwnerOptions] = useState<Array<{ id: string; full_name: string; email?: string | null; role?: string | null }>>([]);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [pipelineDirty, setPipelineDirty] = useState(false);
  const [pipelineSaving, setPipelineSaving] = useState(false);
  const [pipelineSavedAt, setPipelineSavedAt] = useState<Date | null>(null);
  const [qualifyConfirmOpen, setQualifyConfirmOpen] = useState(false);
  const [whatsAppSelection, setWhatsAppSelection] = useState<WhatsAppSelectionState | null>(null);
  const [callSelection, setCallSelection] = useState<CallSelectionState | null>(null);
  const [calling, setCalling] = useState(false);
  const [activeCall, setActiveCall] = useState<VoiceCallStatusResponse | null>(null);
  const [contactsDraft, setContactsDraft] = useState<ProspectContact[]>([]);
  const notes = useMemo(() => prospect?.notes ?? [], [prospect]);
  const dateTimeInputClassName = "w-[220px] max-w-full";
  const contacts = useMemo(() => normalizeContacts(contactsDraft), [contactsDraft]);
  const finalClientTypeSearchItems = useMemo(
    () =>
      finalClientTypeOptions.map((option) => ({
        id: option.value,
        title: option.label,
      })),
    [],
  );

  useEffect(() => {
    if (!open || !prospectId) {
      setProspect(null);
      setWhatsAppSelection(null);
      setCallSelection(null);
      setActiveCall(null);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const data = await getProspect(prospectId);
        setProspect(data);
        setStage(data.stage || "novo");
        setBusinessType((data.business_type as ProspectBusinessType | undefined) ?? "");
        const loadedFinalClientTypes = normalizeFinalClientTypes(
          data.final_client_types ??
          (data as unknown as { final_client_type?: FinalClientType | null }).final_client_type ??
          (data as unknown as { client_types?: string[] | string | null }).client_types,
        );
        setFinalClientTypes(loadedFinalClientTypes);
        setFinalClientTypePicker("");
        setQualifyBusinessType((data.business_type as ProspectBusinessType | undefined) ?? "");
        setQualifyFinalClientTypes(loadedFinalClientTypes);
        setQualifyFinalClientTypePicker("");
        setNextFollowUp(toLocalInput(data.next_follow_up_at));
        setOwnerId(data.owner_id ?? null);
        setPipelineDirty(false);
        setPipelineSavedAt(null);
        setContactsDraft(enforceSinglePriority(normalizeContacts(data.contacts ?? [])));
        try {
          const latestCall = await getLatestVoiceCallStatus(data.id);
          setActiveCall(latestCall);
        } catch (callError: any) {
          if (callError?.status === 404) {
            setActiveCall(null);
          } else {
            console.error("Error loading latest call status:", callError);
          }
        }
      } catch (error: any) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, prospectId, toast]);

  const buildProspectPayload = (): Partial<Prospect> | null => {
    if (!prospect) return null;
    const normalizedStage = isValidStage(stage) ? stage : undefined;
    return {
      company_name: prospect.company_name,
      business_type: businessType || undefined,
      final_client_types: businessType === "final_client" ? finalClientTypes : [],
      country: prospect.country ?? null,
      address: prospect.address ?? null,
      website: prospect.website ?? null,
      referred_from: prospect.referred_from ?? null,
      owner_id: ownerId ?? null,
      stage: normalizedStage,
      next_follow_up_at: fromLocalInput(nextFollowUp),
      last_contact_at: prospect.last_contact_at ?? null,
      contacts: enforceSinglePriority(contacts),
    };
  };

  useEffect(() => {
    if (!pipelineDirty || !prospect) {
      return;
    }
    const timer = setTimeout(async () => {
      const payload = buildProspectPayload();
      if (!payload) return;
      setPipelineSaving(true);
      try {
        const updated = await updateProspect(prospect.id, payload);
        setProspect(updated);
        setOwnerId(updated.owner_id ?? null);
        setContactsDraft(enforceSinglePriority(normalizeContacts(updated.contacts ?? [])));
        setPipelineSavedAt(new Date());
        setPipelineDirty(false);
        onUpdated?.();
      } catch (error: any) {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      } finally {
        setPipelineSaving(false);
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [pipelineDirty, stage, businessType, finalClientTypes, ownerId, nextFollowUp, contacts, prospect, toast, onUpdated]);

  useEffect(() => {
    if (!open || !activeCall?.external_call_id || !activeCall.active) {
      return;
    }

    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const status = await getVoiceCallStatus(activeCall.external_call_id);
        if (cancelled) return;
        setActiveCall(status);
      } catch (error: any) {
        if (cancelled) return;
        if (error?.status === 404) {
          setActiveCall(null);
          return;
        }
        console.error("Error polling call status:", error);
      }
    }, 4000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open, activeCall?.external_call_id, activeCall?.active]);

  const handleAddNote = async () => {
    if (!prospect || !noteText.trim()) return;
    setSaving(true);
    try {
      const updated = await addProspectNote(prospect.id, {
        text: noteText.trim(),
        type: noteType,
        contacted_at: new Date().toISOString(),
        next_follow_up_at: fromLocalInput(nextFollowUp),
      });
      setProspect(updated);
      setOwnerId(updated.owner_id ?? null);
      setContactsDraft(enforceSinglePriority(normalizeContacts(updated.contacts ?? [])));
      setNoteText("");
      onUpdated?.();
      toast({ title: "Note added", description: "Prospect note saved." });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (note: { id?: string | null; created_at?: string | null; text?: string | null; type?: string | null }) => {
    if (!prospect) return;
    setSaving(true);
    try {
      const updated = await deleteProspectNote(prospect.id, {
        id: note.id ?? null,
        created_at: note.created_at ?? null,
        text: note.text ?? null,
        type: note.type ?? null,
      });
      setProspect(updated);
      setOwnerId(updated.owner_id ?? null);
      setContactsDraft(enforceSinglePriority(normalizeContacts(updated.contacts ?? [])));
      onUpdated?.();
      toast({ title: "Note deleted", description: "Prospect note removed." });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const persistContacts = async (nextContacts: ProspectContact[], successMessage: string) => {
    if (!prospect) return;
    const normalized = enforceSinglePriority(normalizeContacts(nextContacts));
    const previous = contactsDraft;
    setContactsDraft(normalized);
    setSaving(true);
    try {
      const payload = buildProspectPayload();
      if (!payload) {
        return;
      }
      payload.contacts = normalized;
      const updated = await updateProspect(prospect.id, payload);
      setProspect(updated);
      setOwnerId(updated.owner_id ?? null);
      setContactsDraft(enforceSinglePriority(normalizeContacts(updated.contacts ?? [])));
      setPipelineDirty(false);
      setPipelineSavedAt(new Date());
      onUpdated?.();
      toast({ title: "Saved", description: successMessage });
    } catch (error: any) {
      setContactsDraft(previous);
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const contactChangeMessageByReason: Record<ContactManagerChangeReason, string> = {
    added: "Contact added.",
    updated: "Contact updated.",
    removed: "Contact removed.",
    priority_updated: "Priority contact updated.",
    priority_removed: "Priority contact removed.",
    no_response_updated: "Contact flag updated.",
  };

  const handleContactsChange = (nextContacts: ProspectContact[], reason: ContactManagerChangeReason) => {
    persistContacts(nextContacts, contactChangeMessageByReason[reason]);
  };

  const canAssignOwner = userRole === "ceo" || userRole === "general_manager";

  const loadOwnerOptions = async () => {
    if (!canAssignOwner) return;
    try {
      setOwnersLoading(true);
      const users = await lookupUsersByRoles(["sales_staff", "general_manager", "ceo"]);
      const normalized = (users || []).map((item) => ({
        id: item.id,
        full_name: item.full_name,
        email: item.email,
        role: item.role,
      }));
      normalized.sort((a, b) => a.full_name.localeCompare(b.full_name));
      setOwnerOptions(normalized);
    } catch (error) {
      console.error("Error loading owners:", error);
    } finally {
      setOwnersLoading(false);
    }
  };

  const handleOwnerChange = (value: string) => {
    const normalized = value === "unassigned" ? null : value;
    setOwnerId(normalized);
    setPipelineDirty(true);
  };

  const handleStageChange = (value: string) => {
    if (value === "qualificacao" && stage !== "qualificacao") {
      setQualifyBusinessType((businessType as ProspectBusinessType | "") || "");
      setQualifyFinalClientTypes(normalizeFinalClientTypes(finalClientTypes));
      setQualifyFinalClientTypePicker("");
      setQualifyConfirmOpen(true);
      return;
    }
    setStage(value);
    setPipelineDirty(true);
  };

  const handleBusinessTypeChange = (value: ProspectBusinessType) => {
    setBusinessType(value);
    if (value !== "final_client") {
      setFinalClientTypes([]);
      setFinalClientTypePicker("");
    }
    setPipelineDirty(true);
  };

  const handleCountryChange = (value: string | number) => {
    const nextCountry = String(value);
    setProspect((previous) => (previous ? { ...previous, country: nextCountry } : previous));
    setPipelineDirty(true);
  };

  const clearCountry = () => {
    setProspect((previous) => (previous ? { ...previous, country: null } : previous));
    setPipelineDirty(true);
  };

  const handleWebsiteChange = (value: string) => {
    setProspect((previous) => (previous ? { ...previous, website: value } : previous));
    setPipelineDirty(true);
  };

  const addFinalClientTypeToPipeline = (value: FinalClientType) => {
    setFinalClientTypes((prev) => normalizeFinalClientTypes([...prev, value]));
    setFinalClientTypePicker("");
    setPipelineDirty(true);
  };

  const removeFinalClientTypeFromPipeline = (value: FinalClientType) => {
    setFinalClientTypes((prev) => prev.filter((item) => item !== value));
    setPipelineDirty(true);
  };

  const addFinalClientTypeToQualify = (value: FinalClientType) => {
    setQualifyFinalClientTypes((prev) => normalizeFinalClientTypes([...prev, value]));
    setQualifyFinalClientTypePicker("");
  };

  const removeFinalClientTypeFromQualify = (value: FinalClientType) => {
    setQualifyFinalClientTypes((prev) => prev.filter((item) => item !== value));
  };

  const handleConfirmQualify = async () => {
    if (!prospect) return;
    if (!qualifyBusinessType) {
      toast({ title: "Validation error", description: "Business type is required.", variant: "destructive" });
      return;
    }
    if (qualifyBusinessType === "final_client" && qualifyFinalClientTypes.length === 0) {
      toast({ title: "Validation error", description: "At least one Final Client Type is required.", variant: "destructive" });
      return;
    }
    setPipelineSaving(true);
    try {
      const result = await qualifyProspectToPartnerWithBusinessType(
        prospect.id,
        qualifyBusinessType,
        qualifyBusinessType === "final_client" ? qualifyFinalClientTypes : [],
      );
      const updated = result.prospect;
      setProspect(updated);
      setStage(updated.stage || "novo");
      setBusinessType((updated.business_type as ProspectBusinessType | undefined) ?? "");
      const updatedFinalClientTypes = normalizeFinalClientTypes(updated.final_client_types);
      setFinalClientTypes(updatedFinalClientTypes);
      setFinalClientTypePicker("");
      setQualifyBusinessType((updated.business_type as ProspectBusinessType | undefined) ?? "");
      setQualifyFinalClientTypes(updatedFinalClientTypes);
      setQualifyFinalClientTypePicker("");
      setOwnerId(updated.owner_id ?? null);
      setContactsDraft(enforceSinglePriority(normalizeContacts(updated.contacts ?? [])));
      setPipelineDirty(false);
      setPipelineSavedAt(new Date());
      onUpdated?.();

      if (result.client) {
        toast({
          title: "Final client created",
          description: `Client ${result.client.company ?? updated.company_name} created from prospect.`,
        });
      } else if (result.created) {
        toast({
          title: "Partner created",
          description: `Partner ${result.partner?.name ?? ""} created from prospect.`,
        });
      } else if (result.already_exists) {
        toast({
          title: "Partner already exists",
          description: "No new partner was created. The existing partner is now linked to this prospect.",
        });
      }
      onOpenChange(false);
      navigate("/prospects");
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setPipelineSaving(false);
      setQualifyConfirmOpen(false);
    }
  };

  const handleNextFollowUpChange = (value: string) => {
    setNextFollowUp(sanitizeDatetimeLocal(value));
    setPipelineDirty(true);
  };

  useEffect(() => {
    if (!open || !canAssignOwner) {
      return;
    }
    loadOwnerOptions();
  }, [open, canAssignOwner]);

  const openEmailComposer = (contact: ProspectContact) => {
    const email = contact.email?.trim();
    if (!email) {
      toast({
        title: "Email unavailable",
        description: "This contact does not have an email address.",
      });
      return;
    }
    window.location.href = `mailto:${email}`;
  };

  const openWhatsAppPicker = (contact: ProspectContact) => {
    const phoneOptions = parsePhoneOptions(contact.telephone);
    if (phoneOptions.length === 0) {
      toast({
        title: "WhatsApp unavailable",
        description: "This contact does not have a valid phone number.",
      });
      return;
    }
    setWhatsAppSelection({
      contactName: contact.name?.trim() || "Contact",
      phoneOptions,
    });
  };

  const sendToWhatsApp = (rawPhone: string) => {
    const normalizedPhone = normalizePhoneForWhatsApp(rawPhone);
    if (!normalizedPhone || normalizedPhone.length < 8) {
      toast({
        title: "Invalid number",
        description: `Could not use "${rawPhone}" for WhatsApp.`,
        variant: "destructive",
      });
      return;
    }
    window.open(`https://wa.me/${normalizedPhone}`, "_blank", "noopener,noreferrer");
    setWhatsAppSelection(null);
  };

  const openCallPicker = (contact: ProspectContact) => {
    const phoneOptions = parsePhoneOptions(contact.telephone);
    if (phoneOptions.length === 0) {
      toast({
        title: "Call unavailable",
        description: "This contact does not have a valid phone number.",
      });
      return;
    }
    setCallSelection({
      contactName: contact.name?.trim() || "Contact",
      phoneOptions,
    });
  };

  const startProspectCall = async (rawPhone: string) => {
    if (!prospect) return;
    setCalling(true);
    try {
      const result = await startVoiceCall({
        destination_phone_number: rawPhone,
        destination_label: callSelection?.contactName ?? null,
        prospect_id: prospect.id,
        mode: "pstn_bridge",
      });
      setActiveCall({
        provider: result.provider,
        external_call_id: result.external_call_id,
        mode: result.mode,
        status: result.status,
        active: result.active,
        prospect_id: prospect.id,
        destination_label: callSelection?.contactName ?? null,
        agent_phone_number: result.agent_phone_number,
        destination_phone_number: result.destination_phone_number,
        started_at: result.started_at,
        ended_at: null,
        updated_at: result.started_at,
      });
      toast({
        title: "Call started",
        description: `Provider: ${result.provider}. Status: ${result.status}.`,
      });
      setCallSelection(null);
    } catch (error: any) {
      toast({
        title: "Error starting call",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCalling(false);
    }
  };


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Prospect</DialogTitle>
        </DialogHeader>
        {activeCall && (
          <div className="rounded-md border border-border bg-muted/20 px-3 py-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Call status</p>
                <p className="text-sm text-foreground">
                  {activeCall.destination_label || activeCall.destination_phone_number}
                </p>
              </div>
              <Badge variant={activeCall.active ? "default" : "outline"}>
                {formatCallStatus(activeCall.status)}
              </Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {`Agent ${activeCall.agent_phone_number} • Updated ${formatDate(activeCall.updated_at)}`}
            </p>
          </div>
        )}

        {loading || !prospect ? (
          <p className="text-sm text-muted-foreground">Loading prospect...</p>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
              <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Company</p>
                  <Badge variant={getStageBadgeVariant(stage)}>{getStageLabel(stage)}</Badge>
                </div>
                <div className="space-y-1">
                  <h2 className="text-xl font-semibold">{prospect.company_name}</h2>
                  <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-2">
                    <span>{prospect.country || "-"}</span>
                    {prospect.address && <span>• {prospect.address}</span>}
                  </div>
                  <div className="space-y-2 pt-2">
                    <label className="text-xs font-medium uppercase text-muted-foreground">Business type</label>
                    <Select value={businessType || undefined} onValueChange={(value) => handleBusinessTypeChange(value as ProspectBusinessType)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select business type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="operator">Operator</SelectItem>
                        <SelectItem value="agency">Agency</SelectItem>
                        <SelectItem value="final_client">Final Client</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-medium uppercase text-muted-foreground">Country</label>
                      {prospect.country && (
                        <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={clearCountry}>
                          Clear
                        </Button>
                      )}
                    </div>
                    <SearchableSelect
                      items={COUNTRY_OPTIONS}
                      value={prospect.country || undefined}
                      onValueChange={handleCountryChange}
                      placeholder="Select a country"
                      emptyText="No country found."
                      fallbackDisplayValue={prospect.country || undefined}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium uppercase text-muted-foreground">Website</label>
                    <Input
                      value={prospect.website || ""}
                      onChange={(event) => handleWebsiteChange(event.target.value)}
                      placeholder="https://example.com"
                    />
                  </div>
                  {businessType === "final_client" && (
                    <div className="space-y-2">
                      <label className="text-xs font-medium uppercase text-muted-foreground">Final client types</label>
                      <SearchableSelect
                        items={finalClientTypeSearchItems}
                        value={finalClientTypePicker || undefined}
                        onValueChange={(value) => addFinalClientTypeToPipeline(String(value) as FinalClientType)}
                        placeholder="Select a final client type"
                        emptyText="No final client type found."
                      />
                      {finalClientTypes.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {finalClientTypes.map((type) => {
                            const label = finalClientTypeOptions.find((option) => option.value === type)?.label || type;
                            return (
                              <Badge key={type} variant="secondary" className="flex items-center gap-1 pr-1">
                                <span>{label}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5"
                                  onClick={() => removeFinalClientTypeFromPipeline(type)}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </Badge>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {prospect.referred_from && (
                    <p className="text-xs text-muted-foreground">Referred from: {prospect.referred_from}</p>
                  )}
                </div>
              </div>

              <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pipeline</p>
                  <span className="text-xs text-muted-foreground">
                    {pipelineSaving
                      ? "Saving..."
                      : pipelineDirty
                        ? "Unsaved changes"
                        : pipelineSavedAt
                          ? "Saved"
                          : "Auto-save"}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-xs font-medium uppercase text-muted-foreground">Stage</label>
                    <Select value={stage} onValueChange={handleStageChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {stageOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium uppercase text-muted-foreground">Next follow-up</label>
                    <Input
                      type="datetime-local"
                      value={nextFollowUp}
                      onChange={(e) => handleNextFollowUpChange(e.target.value)}
                      onClick={(e) => openNativeDateTimePicker(e.currentTarget)}
                      onFocus={(e) => openNativeDateTimePicker(e.currentTarget)}
                      className={dateTimeInputClassName}
                      disabled={saving || pipelineSaving}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium uppercase text-muted-foreground">Responsible</label>
                    {canAssignOwner ? (
                      <Select value={ownerId ?? ""} onValueChange={handleOwnerChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select owner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned</SelectItem>
                          {ownersLoading && (
                            <SelectItem value="loading" disabled>
                              Loading...
                            </SelectItem>
                          )}
                          {ownerOptions.map((option) => (
                            <SelectItem key={option.id} value={option.id}>
                              {option.full_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={prospect.owner_name || user?.full_name || "-"}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
              <ContactManagerPanel
                contacts={contacts}
                disabled={saving}
                onChange={handleContactsChange}
                onSendEmail={openEmailComposer}
                onSendWhatsApp={openWhatsAppPicker}
                onSendCall={openCallPicker}
                onMissingInfo={(payload) => {
                  toast({ title: payload.title, description: payload.description });
                }}
              />

              <div className="rounded-lg border border-border bg-card p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Activity</p>
                  <span className="text-xs text-muted-foreground">Prospect timeline</span>
                </div>
                <div className="rounded-lg border border-border bg-background p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Add note</p>
                    <span className="text-xs text-muted-foreground">Applies to the prospect</span>
                  </div>
                  <Select value={noteType} onValueChange={setNoteType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select note type" />
                    </SelectTrigger>
                    <SelectContent>
                      {noteTypeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Textarea
                    rows={4}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Write a note about the contact..."
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleAddNote} disabled={saving || !noteText.trim()}>
                      {saving ? "Saving..." : "Add note"}
                    </Button>
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-background p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Activity log</p>
                    <span className="text-xs text-muted-foreground">{notes.length}</span>
                  </div>
                  {notes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No notes yet.</p>
                  ) : (
                    <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                      {notes
                        .slice()
                        .reverse()
                        .map((note, index) => (
                          <div key={`${note.created_at}-${index}`} className="rounded-md border border-border p-3 space-y-1">
                            <div className="flex items-center justify-between">
                              <Badge variant="outline">{note.type || "note"}</Badge>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">{formatDate(note.created_at)}</span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={saving}
                                  onClick={() => handleDeleteNote(note)}
                                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-sm text-foreground whitespace-pre-wrap">{note.text}</p>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </DialogContent>
      <Dialog
        open={Boolean(whatsAppSelection)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setWhatsAppSelection(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select WhatsApp number</DialogTitle>
            <DialogDescription>
              {`Choose which number to message ${whatsAppSelection?.contactName ?? "this contact"}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {whatsAppSelection?.phoneOptions.map((phone) => (
              <Button
                key={phone}
                type="button"
                variant="outline"
                className="w-full justify-start"
                onClick={() => sendToWhatsApp(phone)}
              >
                <MessageCircle className="mr-2 h-4 w-4" />
                {phone}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(callSelection)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !calling) {
            setCallSelection(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Select number to call</DialogTitle>
            <DialogDescription>
              {`Choose which number to call for ${callSelection?.contactName ?? "this contact"}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {callSelection?.phoneOptions.map((phone) => (
              <Button
                key={phone}
                type="button"
                variant="outline"
                className="w-full justify-start"
                onClick={() => startProspectCall(phone)}
                disabled={calling}
              >
                <Phone className="mr-2 h-4 w-4" />
                {phone}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog open={qualifyConfirmOpen} onOpenChange={setQualifyConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {qualifyBusinessType === "final_client"
                ? "Create Final Client from this prospect?"
                : "Create Partner from this prospect?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {qualifyBusinessType === "final_client"
                ? "Confirming will mark this prospect as Qualified and create a new Final Client."
                : "Confirming will mark this prospect as Qualified and attempt to create a Partner using company name and country. If a Partner with the same key already exists, nothing new will be created and the existing Partner will be used."}
            </AlertDialogDescription>
            <div className="space-y-2">
              <label className="text-xs font-medium uppercase text-muted-foreground">Business type *</label>
              <Select
                value={qualifyBusinessType || undefined}
                onValueChange={(value) => {
                  const next = value as ProspectBusinessType;
                  setQualifyBusinessType(next);
                  if (next !== "final_client") {
                    setQualifyFinalClientTypes([]);
                    setQualifyFinalClientTypePicker("");
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="agency">Agency</SelectItem>
                  <SelectItem value="final_client">Final Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {qualifyBusinessType === "final_client" && (
              <div className="space-y-2">
                <label className="text-xs font-medium uppercase text-muted-foreground">Final client types *</label>
                <SearchableSelect
                  items={finalClientTypeSearchItems}
                  value={qualifyFinalClientTypePicker || undefined}
                  onValueChange={(value) => addFinalClientTypeToQualify(String(value) as FinalClientType)}
                  placeholder="Select a final client type"
                  emptyText="No final client type found."
                />
                {qualifyFinalClientTypes.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {qualifyFinalClientTypes.map((type) => {
                      const label = finalClientTypeOptions.find((option) => option.value === type)?.label || type;
                      return (
                        <Badge key={type} variant="secondary" className="flex items-center gap-1 pr-1">
                          <span>{label}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5"
                            onClick={() => removeFinalClientTypeFromQualify(type)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pipelineSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmQualify}
              disabled={pipelineSaving || !qualifyBusinessType || (qualifyBusinessType === "final_client" && qualifyFinalClientTypes.length === 0)}
            >
              {pipelineSaving ? "Processing..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default ProspectDetailDialog;
